// Pure helpers for the in-pool off-hours / pause banners. Kept out of the route
// component so they're unit-testable in the node test env (no DOM).
import { isPoolJoinable, type OffHours, type PoolInfo } from './protocol';

// Which pause banner a joined SDR should see for their active pool, or null for
// none. Precedence: a deploy drain (its own banner) suppresses both; an operator
// pause wins over off-hours (it's the actionable fact); off-hours is the fallback.
// A joined SDR making status === 'paused' is always an operator pause — a no-SDRs
// pause can't happen while they're in the pool.
export function poolPauseBanner(
  pool: Pick<PoolInfo, 'status' | 'offHours'> | undefined,
  deploying: boolean,
): 'operator' | 'off-hours' | null {
  if (!pool || deploying) return null;
  if (pool.status === 'paused') return 'operator';
  if (pool.offHours) return 'off-hours';
  return null;
}

// The resume instant as a wall clock in the LEAD's zone, not the SDR's — "10:00 AM"
// means 10am where the lead is. Locale is pinned so the copy reads the same for every
// SDR. Empty when the zone is missing or either input is one Intl rejects: this line
// is a courtesy next to the countdown, and must not take the cockpit down with it.
export function formatResumeAt(at: string, timeZone: string | undefined): string {
  if (!timeZone) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(new Date(at));
  } catch {
    return '';
  }
}

// The core reports a pool whose every dialable lead is asleep under its own run_state
// rather than as `open` with a side-object, so it lands outside JOINABLE_POOL_STATES
// and the picker refuses it for exactly the reason it refuses a closed pool. Before
// that, off-hours rode alongside `open` and the card looked completely joinable.
export const OFF_HOURS_STATUS = 'off_hours';

export function isOffHours(pool: Pick<PoolInfo, 'status'>): boolean {
  return pool.status === OFF_HOURS_STATUS;
}

// What the PICKER card says about an unavailable pool: the reason, never the time.
//
// Withholding the timing is the point. An SDR who can see that their favourite pool
// wakes in twenty minutes waits for it instead of taking work that is available now,
// and the pool they are waiting on may not have leads for them when it opens. The
// in-pool banner does show timings — by then they are already in the pool and the
// question is "should I stay", not "which should I pick".
//
// Null for a pool whose reason is not about availability, where the card's own subline
// (lifecycle, mic) is the better explanation.
export function unavailableReasonLabel(
  pool: Pick<PoolInfo, 'status' | 'offHours' | 'redialDue'>,
): string | null {
  if (!isOffHours(pool)) return null;
  // A dark pool whose gate is due IS joinable — the join is what runs its next pass — so
  // it gets the card's join copy rather than a reason it is unavailable.
  if (pool.redialDue === true) return null;
  // One string, because a snapshot only exists when some zone is asleep — a pool also
  // holding resting leads is still held by the clock, which is the wait the SDR cannot
  // sit out. A pool held ONLY by resting leads produces no snapshot and never gets here.
  return 'Outside calling hours';
}

// The one-line "when can I work this?" for the IN-POOL banner, e.g.
// "US Eastern opens 8:00 AM · in ~2 h". Never the picker — see `unavailableReasonLabel`.
//
// Counts down to `nextReturnAt`, which the core sets to whichever population returns
// first, so every surface shows the same instant.
export function offHoursSummary(pool: Pick<PoolInfo, 'offHours'>): string {
  const offHours = pool.offHours;
  const resume = offHours?.soonestResume;
  const returnAt = offHours?.nextReturnAt;
  if (!returnAt) return 'Dialing resumes when your leads’ local calling hours open.';

  const countdown = formatResumeIn(returnAt);
  // The zone label only describes the resume instant, so it may only be attached when
  // that is the instant being shown. Otherwise the line would name a zone opening at a
  // time that is really a lead's rung lapsing.
  if (!resume || returnAt !== resume.at) return `Leads return ${countdown}`;

  const at = formatResumeAt(resume.at, resume.timezone);
  return at ? `${resume.label} opens ${at} · ${countdown}` : `${resume.label} opens ${countdown}`;
}

// The one-line eviction warning for a seated SDR whose pool has an armed dark-pool
// grace: they are about to be returned to the picker, and can rejoin once the window
// opens. Null when no eviction is coming (the ordinary waiting banner suffices) or
// the deadline already passed (the pool_left frame is about to land; don't promise a
// removal that is already happening).
export function evictionWarning(
  offHours: Pick<OffHours, 'evictingAt' | 'soonestResume' | 'nextReturnAt'>,
): string | null {
  if (!offHours.evictingAt) return null;
  if (new Date(offHours.evictingAt).getTime() <= Date.now()) return null;
  const resume = offHours.soonestResume;
  const returnAt = offHours.nextReturnAt ?? resume?.at;
  let reopens = 'You can rejoin when this pool has leads to dial.';
  if (returnAt != null && resume != null && returnAt === resume.at) {
    const when = formatResumeAt(resume.at, resume.timezone) || formatResumeIn(resume.at);
    reopens = `You can rejoin when calling hours open (${resume.label} at ${when}).`;
  } else if (returnAt != null) {
    // Resting leads, not a window: there is no zone opening to name, and saying there
    // was would send the SDR back at the wrong time.
    reopens = `You can rejoin ${formatResumeIn(returnAt)}.`;
  }
  return `Returning you to the pool picker in a few seconds — ${reopens}`;
}

// Coarse "in ~47 min" / "in ~3 h" — the resume time is a point-in-time snapshot
// that drifts between polls, so precision would be false confidence. Past/invalid
// instants read as "soon".
export function formatResumeIn(at: string): string {
  const ms = new Date(at).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 'soon';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `in ~${minutes} min`;
  const hours = Math.round(minutes / 60);
  // A ring budget runs to a week, and "in ~168 h" is not a duration anyone reads.
  return hours < 48 ? `in ~${hours} h` : `in ~${Math.round(hours / 24)} days`;
}

// How long until a finished pool can be dialed again. Null — not "in 0m" — once the
// instant has passed or if it will not parse: the line should disappear rather than tell
// an SDR to wait on a pool that is ready. Rounds up, so 30s away reads "in 1m".
export function redialCountdown(
  redialableAt: string | null | undefined,
  now: number,
): string | null {
  if (!redialableAt) return null;

  const at = Date.parse(redialableAt);
  if (Number.isNaN(at)) return null;

  const minutes = Math.ceil((at - now) / 60_000);
  if (minutes <= 0) return null;

  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  const mins = minutes % 60;

  if (days > 0) return hours > 0 ? `in ${days}d ${hours}h` : `in ${days}d`;
  if (hours > 0) return mins > 0 ? `in ${hours}h ${mins}m` : `in ${hours}h`;
  return `in ${mins}m`;
}

// What a pool card says and whether it can be clicked. Out of the route because
// dialer-web has no component-rendering setup, so inline in JSX this could only be
// verified by reading it.
//
// The load-bearing case is a DUE pool that is otherwise unjoinable — `closed`, or dark.
// The redial trigger hangs off the join request, so a due pool whose card stayed disabled
// would make the automation unreachable for exactly the pools it exists to serve.
export function poolCardState(
  pool: { status: string; redialableAt?: string | null; redialDue?: boolean; waiting: number },
  opts: { micBlocked: boolean; micUnchecked: boolean; nowMs: number },
): { joinable: boolean; subline: string } {
  const lifecycleJoinable = isPoolJoinable(pool.status);
  const countdown = redialCountdown(pool.redialableAt, opts.nowMs);
  // The server decides this, never the local clock: the BFF fires the join without waiting
  // for an answer, so a fast workstation would land the SDR in a pool the core has no
  // session for. The countdown TEXT may drift a minute; clickability may not.
  const dueForRedial = pool.redialDue === true;

  if (!lifecycleJoinable && !dueForRedial) {
    return {
      joinable: false,
      // No countdown on an off-hours card: the picker withholds timings so an SDR takes
      // available work rather than waiting for a favourite pool. `unavailableReasonLabel`
      // supplies that card's copy instead.
      subline:
        countdown && pool.status === 'closed'
          ? `Finished its pass — ready for more ${countdown}`
          : `Not accepting joins (${pool.status})`,
    };
  }

  if (opts.micBlocked) return { joinable: false, subline: 'Microphone unavailable' };
  if (opts.micUnchecked) return { joinable: false, subline: 'Check your mic first' };

  if (dueForRedial) {
    return {
      joinable: true,
      subline:
        pool.status === OFF_HOURS_STATUS
          ? 'Leads are ready again — join to start the next pass'
          : 'Finished its pass — join to start the next one',
    };
  }

  return {
    joinable: true,
    subline: `${pool.waiting} SDR${pool.waiting === 1 ? '' : 's'} calling`,
  };
}
